/**
 * Quick-look slide-in for a single inbound payment. Mirrors
 * PaymentIntentSheet — operators get the same affordance from the
 * inbound-payments list as from the intents list.
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, ExternalLink, ShieldAlert } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { getInboundPayment } from "@/lib/api";
import { formatMinorUnits } from "@/lib/format";
import { INBOUND_STATE_BADGE_VARIANT } from "@/lib/state-badges";

export function InboundPaymentSheet({
  id,
  open,
  onOpenChange,
}: {
  id: string | null;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const query = useQuery({
    queryKey: ["inbound-payment", id] as const,
    queryFn: () => getInboundPayment(id ?? ""),
    enabled: Boolean(id) && open,
  });

  const ip = query.data;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full max-w-md sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>Inbound payment</SheetTitle>
          <SheetDescription>
            Quick look — full record on the detail page.
          </SheetDescription>
        </SheetHeader>

        {query.isLoading || !ip ? (
          <div className="mt-6 space-y-4">
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <div className="mt-2 flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto pr-1">
            <div className="space-y-1">
              <div className="font-mono text-3xl font-semibold tabular-nums">
                {formatMinorUnits(ip.amount, ip.currency)}
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <Badge variant={INBOUND_STATE_BADGE_VARIANT[ip.state]} className="capitalize">
                  {ip.state}
                </Badge>
                <span className="text-muted-foreground">·</span>
                <span>{ip.plugin}</span>
              </div>
            </div>

            <Separator />

            <dl className="grid grid-cols-1 gap-3 text-sm">
              <Field label="Payment ID" value={<code className="font-mono text-xs">{ip.id}</code>} />
              <Field
                label="Tenant"
                value={
                  <>
                    {ip.tenant_name}
                    <Badge variant="muted" className="ml-2 capitalize">
                      {ip.tenant_environment}
                    </Badge>
                  </>
                }
              />
              <Field
                label="Provider ref"
                value={<code className="font-mono text-xs">{ip.provider_ref}</code>}
              />
              <Field
                label="Account ref"
                value={<code className="font-mono text-xs">{ip.account_ref || "—"}</code>}
              />
              <Field label="Received" value={new Date(ip.received_at).toLocaleString()} />
            </dl>

            {Object.keys(ip.payer).length > 0 ? (
              <div className="space-y-2">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Payer (masked)
                </div>
                <dl className="grid grid-cols-1 gap-2 text-sm">
                  {Object.entries(ip.payer).map(([k, v]) => (
                    <Field
                      key={k}
                      label={k}
                      value={
                        <code className="font-mono text-xs">
                          {typeof v === "string" ? v : JSON.stringify(v)}
                        </code>
                      }
                    />
                  ))}
                </dl>
              </div>
            ) : null}

            <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/5 p-3 text-xs text-warning">
              <ShieldAlert className="h-4 w-4 shrink-0" />
              <div>
                <div className="font-medium">Raw callback withheld</div>
                <p className="mt-0.5 text-warning/80">
                  Unmasked MSISDN + payer PII requires step-up auth (coming in PR-6).
                </p>
              </div>
            </div>

            <div className="mt-auto flex gap-2 pt-4">
              <Button asChild className="flex-1">
                <Link to={`/inbound-payments/${ip.id}`}>
                  Open full page
                  <ArrowUpRight />
                </Link>
              </Button>
              <Button variant="outline" size="icon" asChild aria-label="Open in new tab">
                <a href={`/inbound-payments/${ip.id}`} target="_blank" rel="noreferrer">
                  <ExternalLink />
                </a>
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="shrink-0 text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="min-w-0 max-w-[60%] text-right text-sm font-medium">{value}</dd>
    </div>
  );
}
